import { Box, Grid, Typography, Button, IconButton } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useBooking } from '../../../../context/bookingContext';
import { useState, useEffect, useRef } from 'react';
import QuantityControl from './QuantityControl';
import PackageAndUpsellSelectSkeleton from './PackageAndUpsellSelectSkeleton';

const getMinimumCount = upgrade => {
  const value = Number(upgrade?.minimumCount);
  return Number.isFinite(value) && value >= 0 ? Math.floor(value) : null;
};

function PackageAndUpsellSelect({ isLoading }) {
  const theme = useTheme();
  const [packagesData, setPackagesData] = useState([]);
  const [upsellsData, setUpsellsData] = useState([]);
  const [upsellQuantities, setUpsellQuantities] = useState({});
  const [expandedByPackageId, setExpandedByPackageId] = useState({});
  const previousSelectedUpsellsRef = useRef([]);
  const {
    selectedPackage,
    setSelectedPackage,
    selectedUpsells,
    setSelectedUpsells,
    selectedCategories,
    selectedEntertainmentObject,
    setPackageTags,
    selectedProduct,
    packages,
    upsells,
    getCurrentTags,
    fetchPackages,
    fetchUpsells,
    isPackagesLoading,
    isUpsellsLoading,
    calculateLineItems,
    selectedPricingId,
    selectedDuration,
    priceOverrides,
  } = useBooking();

  useEffect(() => {
    if (selectedProduct) {
      fetchPackages();
      fetchUpsells();
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (packages) {
      // Transform the packages data to extract the upgrade information
      const transformedPackages = packages.map(item => ({
        id: item.upgradeId,
        ...item.upgrade,
        sortKey: item.sortKey,
        activeInThisProduct: item.activeInThisProduct,
      }));
      setPackagesData(transformedPackages);
    } else {
      setPackagesData([]);
    }
  }, [packages]);

  useEffect(() => {
    if (upsells) {
      // Transform the upsells data to extract the upgrade information
      const transformedUpsells = upsells.map(item => ({
        id: item.upgradeId,
        ...item.upgrade,
        sortKey: item.sortKey,
        activeInThisProduct: item.activeInThisProduct,
      }));
      setUpsellsData(transformedUpsells);
    } else {
      setUpsellsData([]);
      setUpsellQuantities({});
    }
  }, [upsells]);

  // Separate useEffect for quantity management
  useEffect(() => {
    if (upsellsData.length > 0) {
      // Initialize quantities for all upsells, respecting existing selections
      const initialQuantities = {};
      const totalPersons = selectedCategories.reduce((sum, category) => {
        const q = category?.quantity ?? category?.count ?? 0;
        return sum + (parseInt(String(q), 10) || 0);
      }, 0);
      const laneDensityRaw = Number(selectedEntertainmentObject?.participantLaneDensity || 0);
      const laneDensity =
        Number.isFinite(laneDensityRaw) && laneDensityRaw > 0 ? laneDensityRaw : null;
      const totalLanes = laneDensity ? Math.ceil(totalPersons / laneDensity) : totalPersons;

      upsellsData.forEach(upsell => {
        const existingUpsell = selectedUpsells.find(u => u.id === upsell.id);
        let quantity = existingUpsell ? existingUpsell.quantity : 0;

        if (typeof upsell.maximumCount === 'number') {
          quantity = Math.min(quantity, upsell.maximumCount);
        }

        // Adjust quantities for matchPersons, matchPersonsNoCancel and matchLanesNoCancel types
        if (
          (upsell.type === 'matchPersons' || upsell.type === 'matchPersonsNoCancel') &&
          quantity > 0
        ) {
          quantity = totalPersons;
        }
        if (upsell.type === 'matchLanesNoCancel' && quantity > 0) {
          quantity = totalLanes;
        }
        if (upsell.type === 'freeSelectMaxPersons' && quantity > totalPersons) {
          quantity = totalPersons;
        }

        initialQuantities[upsell.id] = quantity;
      });

      setUpsellQuantities(initialQuantities);

      // Update selected upsells in context if needed
      const updatedSelectedUpsells = upsellsData
        .filter(upsell => initialQuantities[upsell.id] > 0)
        .map(upsell => ({
          ...upsell,
          quantity: initialQuantities[upsell.id],
        }));

      // Only update if the selected upsells have actually changed
      const currentSelectedUpsellsString = JSON.stringify(updatedSelectedUpsells);
      const previousSelectedUpsellsString = JSON.stringify(previousSelectedUpsellsRef.current);

      if (currentSelectedUpsellsString !== previousSelectedUpsellsString) {
        setSelectedUpsells(updatedSelectedUpsells);
        previousSelectedUpsellsRef.current = updatedSelectedUpsells;
      }
    } else {
      // If no upsells are available, clear all selected upsells
      if (selectedUpsells.length > 0) {
        setSelectedUpsells([]);
        setUpsellQuantities({});
        previousSelectedUpsellsRef.current = [];
      }
    }
  }, [upsellsData, selectedCategories, selectedEntertainmentObject, setSelectedUpsells, selectedUpsells]);

  // Keep line items in sync while selecting packages/upsells so totals can be shown live
  useEffect(() => {
    calculateLineItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPackage,
    selectedUpsells,
    selectedCategories,
    selectedPricingId,
    selectedDuration,
    priceOverrides,
  ]);

  if (isLoading || isPackagesLoading || isUpsellsLoading) {
    return <PackageAndUpsellSelectSkeleton />;
  }

  const totalPersons = selectedCategories.reduce((sum, category) => {
    const q = category?.quantity ?? category?.count ?? 0;
    return sum + (parseInt(String(q), 10) || 0);
  }, 0);

  const calculatePackagePrice = pkg => {
    const totalPersons = selectedCategories.reduce((sum, category) => {
      const q = category?.quantity ?? category?.count ?? 0;
      return sum + (parseInt(String(q), 10) || 0);
    }, 0);
    const laneDensityRaw = Number(selectedEntertainmentObject?.participantLaneDensity || 0);
    const laneDensity =
      Number.isFinite(laneDensityRaw) && laneDensityRaw > 0 ? laneDensityRaw : null;
    const totalLanes = laneDensity ? Math.ceil(totalPersons / laneDensity) : totalPersons;

    if (pkg.type === 'matchPersons' || pkg.type === 'matchPersonsNoCancel') {
      return pkg.price * totalPersons;
    }
    if (pkg.type === 'matchLanesNoCancel') {
      return pkg.price * totalLanes;
    }
    return pkg.price;
  };

  const handlePackageSelect = async pkg => {
    const isDeselecting = selectedPackage?.id === pkg.id;

    if (isDeselecting) {
      setPackageTags([]);
      setSelectedPackage(null);
      // Keep time-based tags (reservation tags) active when deselecting a package
      const tagsWithoutPackage = getCurrentTags(false, true);
      await fetchUpsells(tagsWithoutPackage);
    } else {
      // Selecting new package
      const packageWithCalculatedPrice = {
        ...pkg,
        calculatedPrice: calculatePackagePrice(pkg),
      };

      setPackageTags(pkg.tags);

      const newTags = getCurrentTags(false, true).concat(pkg.tags);

      setSelectedPackage(packageWithCalculatedPrice);

      // fetch upsells with new tags after package selection
      if (pkg.tags && pkg.tags.length > 0) {
        await fetchUpsells(newTags);
      }
    }
  };

  const handleUpsellQuantityChange = (upsellId, value) => {
    const upsell = upsellsData.find(u => u.id === upsellId);
    let newQuantity = parseInt(value) || 0;
    const totalPersons = selectedCategories.reduce((sum, category) => {
      const q = category?.quantity ?? category?.count ?? 0;
      return sum + (parseInt(String(q), 10) || 0);
    }, 0);
    const laneDensityRaw = Number(selectedEntertainmentObject?.participantLaneDensity || 0);
    const laneDensity =
      Number.isFinite(laneDensityRaw) && laneDensityRaw > 0 ? laneDensityRaw : null;
    const totalLanes = laneDensity ? Math.ceil(totalPersons / laneDensity) : totalPersons;
    const currentQuantity = upsellQuantities[upsellId] || 0;

    if (upsell.type === 'matchPersons' || upsell.type === 'matchPersonsNoCancel') {
      newQuantity = newQuantity < currentQuantity ? 0 : totalPersons;
    } else if (upsell.type === 'matchLanesNoCancel') {
      newQuantity = newQuantity < currentQuantity ? 0 : totalLanes;
    } else if (upsell.type === 'freeSelectMaxPersons') {
      newQuantity = Math.min(Math.max(0, newQuantity), totalPersons);
    } else if (upsell.type === 'freeSelectNoCancel') {
      const maxAllowed = typeof upsell.maximumCount === 'number' ? upsell.maximumCount : undefined;
      newQuantity = Math.max(0, newQuantity);
      if (maxAllowed !== undefined) {
        newQuantity = Math.min(maxAllowed, newQuantity);
      }
    } else {
      // For regular upsells, just ensure it's not negative
      newQuantity = Math.max(0, newQuantity);
    }

    // Update local state
    setUpsellQuantities(prev => ({
      ...prev,
      [upsellId]: newQuantity,
    }));

    // Update context state
    const newSelectedUpsells = upsellsData
      .filter(upsell => {
        const quantity = upsell.id === upsellId ? newQuantity : upsellQuantities[upsell.id];
        return quantity > 0;
      })
      .map(upsell => ({
        ...upsell,
        quantity: upsell.id === upsellId ? newQuantity : upsellQuantities[upsell.id],
      }));
    setSelectedUpsells(newSelectedUpsells);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {packagesData.length > 0 && (
          <Grid container spacing={2} sx={{ width: '100%', ml: { xs: 0, sm: 2 }, mt: 1 }}>
            {packagesData.map(pkg => {
              const minimumCount = getMinimumCount(pkg);
              const isBelowMinimum = minimumCount !== null && totalPersons < minimumCount;
              const minimumHint =
                minimumCount !== null
                  ? `Mindestens ${minimumCount} Teilnehmer erforderlich`
                  : null;
              const fullDesc =
                typeof pkg.description === 'string' ? pkg.description.trim() : '';
              const shortDesc =
                typeof pkg.descriptionShort === 'string' ? pkg.descriptionShort.trim() : '';
              const canToggleDescription =
                Boolean(fullDesc) &&
                Boolean(shortDesc) &&
                !(fullDesc.length === shortDesc.length && fullDesc === shortDesc);
              const isExpanded = Boolean(expandedByPackageId[pkg.id]);
              const displayDesc = canToggleDescription && isExpanded ? fullDesc : shortDesc || fullDesc;

              const isSelectedPkg = selectedPackage?.id === pkg.id;
              return (
                <Grid item key={pkg.id} xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    {canToggleDescription && (
                      <IconButton
                        size='small'
                        aria-label='Beschreibung ein- oder ausklappen'
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          setExpandedByPackageId(prev => ({
                            ...prev,
                            [pkg.id]: !prev[pkg.id],
                          }));
                        }}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          zIndex: 1,
                          color: isSelectedPkg ? 'primary.contrastText' : 'text.secondary',
                          padding: 0.25,
                          '& .MuiSvgIcon-root': { fontSize: 18 },
                          '&:hover': {
                            backgroundColor: isSelectedPkg
                              ? alpha(theme.palette.primary.contrastText, 0.12)
                              : 'action.hover',
                          },
                        }}
                      >
                        <InfoOutlinedIcon fontSize='inherit' />
                      </IconButton>
                    )}
                    <Button
                      variant='outlined'
                      onClick={() => handlePackageSelect(pkg)}
                      disabled={isBelowMinimum}
                      sx={{
                        width: '100%',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        justifyContent: 'center',
                        alignItems: 'center',
                        whiteSpace: 'normal',
                        padding: 1,
                        height: '100%',
                        borderColor: isSelectedPkg ? 'primary.main' : 'divider',
                        backgroundColor: isSelectedPkg ? 'primary.main' : 'transparent',
                        color: isSelectedPkg ? 'primary.contrastText' : 'text.primary',
                        '&:hover': {
                          borderColor: isSelectedPkg ? 'primary.dark' : 'text.secondary',
                          backgroundColor: isSelectedPkg
                            ? 'primary.dark'
                            : 'action.hover',
                        },
                      }}
                    >
                      <Typography variant='body2'>{pkg.name}</Typography>
                      {pkg.price !== undefined && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant='caption'>
                            {pkg.type === 'matchPersons' || pkg.type === 'matchPersonsNoCancel'
                              ? `${pkg.price.toFixed(2).replace('.', ',')}€ pro person`
                              : pkg.type === 'matchLanesNoCancel'
                                ? `${pkg.price.toFixed(2).replace('.', ',')}€ pro Bahn`
                                : `${pkg.price.toFixed(2).replace('.', ',')}€`}
                          </Typography>
                          {(pkg.type === 'matchPersons' ||
                            pkg.type === 'matchPersonsNoCancel' ||
                            pkg.type === 'matchLanesNoCancel') && (
                            <Typography
                              variant='caption'
                              sx={{
                                fontSize: '0.7rem',
                                color: isSelectedPkg
                                  ? alpha(theme.palette.primary.contrastText, 0.85)
                                  : 'text.secondary',
                              }}
                            >
                              ({calculatePackagePrice(pkg).toFixed(2).replace('.', ',')}€ insg.)
                            </Typography>
                          )}
                        </Box>
                      )}
                      {(displayDesc || canToggleDescription) && (
                        <Typography
                          variant='caption'
                          sx={{
                            color: isSelectedPkg
                              ? alpha(theme.palette.primary.contrastText, 0.85)
                              : 'text.secondary',
                          }}
                        >
                          {displayDesc}
                        </Typography>
                      )}
                      {isBelowMinimum && (
                        <Typography variant='caption' sx={{ color: 'grey.500' }}>
                          {minimumHint}
                        </Typography>
                      )}
                    </Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Grid>

      <Grid container spacing={2}>
        {upsellsData.length > 0 &&
          upsellsData.map(upsell => {
            const minimumCount = getMinimumCount(upsell);
            const isBelowMinimum = minimumCount !== null && totalPersons < minimumCount;
            return (
              <Grid item xs={12} key={upsell.id}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  '& > *:first-of-type': {
                    flex: 1,
                  },
                  '& > *:last-child': {
                    flex: { xs: '0 0 auto', sm: '0 0 auto' },
                    width: { xs: '100%', sm: 'auto' },
                    alignSelf: { xs: 'center', sm: 'auto' },
                  },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant='body1'>{upsell.name}</Typography>
                  {upsell.descriptionShort && (
                    <Typography variant='caption' color='text.secondary'>
                      {upsell.descriptionShort}
                    </Typography>
                  )}
                  {upsell.price && (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant='caption' display='block'>
                        {upsell.type === 'matchPersons' ||
                        upsell.type === 'matchPersonsNoCancel' ||
                        upsell.type === 'freeSelectMaxPersons'
                          ? `${upsell.price.toFixed(2).replace('.', ',')}€ pro person`
                          : upsell.type === 'matchLanesNoCancel'
                            ? `${upsell.price.toFixed(2).replace('.', ',')}€ pro Bahn`
                            : `${upsell.price.toFixed(2).replace('.', ',')}€`}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                          fontSize: '0.7rem',
                          visibility:
                            (upsell.type === 'matchPersons' ||
                              upsell.type === 'matchPersonsNoCancel' ||
                              upsell.type === 'matchLanesNoCancel' ||
                              upsell.type === 'freeSelectMaxPersons') &&
                            upsellQuantities[upsell.id] > 0
                              ? 'visible'
                              : 'hidden',
                          height: '0.7rem',
                        }}
                      >
                        ({(upsell.price * upsellQuantities[upsell.id]).toFixed(2).replace('.', ',')}
                        € insg.)
                      </Typography>
                    </Box>
                  )}
                  {isBelowMinimum && (
                    <Typography variant='caption' sx={{ color: 'grey.500' }}>
                      Mindestens {minimumCount} Teilnehmer erforderlich
                    </Typography>
                  )}
                </Box>
                <QuantityControl
                  value={upsellQuantities[upsell.id] || 0}
                  onChange={value => handleUpsellQuantityChange(upsell.id, value)}
                  min={0}
                  max={
                    upsell.type === 'matchPersons' ||
                    upsell.type === 'matchPersonsNoCancel' ||
                    upsell.type === 'matchLanesNoCancel'
                      ? 1
                      : upsell.type === 'freeSelectMaxPersons'
                        ? selectedCategories.reduce(
                            (sum, category) => sum + (parseInt(category.quantity) || 0),
                            0
                          )
                        : typeof upsell.maximumCount === 'number'
                          ? upsell.maximumCount
                          : undefined
                  }
                  disabled={isBelowMinimum}
                />
              </Box>
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
}

export default PackageAndUpsellSelect;
